import { useCallback, useEffect, useRef, useState } from "react";
import {
  Buildings,
  CaretDown,
  CaretRight,
  Check,
  CurrencyDollar,
  Envelope,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { getSubJobs, createSubJob, updateSubJob, archiveSubJob, requestPo, sendInvoice } from "../../../api/projects";

const STATUS_BADGES = {
  active: { label: "Active", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  archived: { label: "Archived", className: "bg-slate-50 text-slate-500 border-slate-200" },
  invoiced: { label: "Invoiced", className: "bg-blue-50 text-blue-700 border-blue-200" },
};

const XERO_QUOTE_BADGES = {
  DRAFT: "bg-slate-100 text-slate-600",
  SENT: "bg-amber-50 text-amber-700",
  ACCEPTED: "bg-emerald-50 text-emerald-700",
  DECLINED: "bg-red-50 text-red-600",
};

function StatusBadge({ status }) {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.active;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function PoRequestDropdown({ projectId, subJobId, poStatus, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Hide if PO already requested/received
  if (poStatus && poStatus !== "none") return null;

  const options = [
    { label: "Send email requesting PO", mode: "send" },
    { label: "Create draft in Outlook", mode: "draft" },
    { label: "Send + auto-invoice on PO", mode: "send_and_auto_invoice" },
  ];

  const handleSelect = async (mode) => {
    setOpen(false);
    setLoading(true);
    try {
      await requestPo(projectId, mode, subJobId);
      toast.success(mode === "draft" ? "PO request draft created" : "PO request sent");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to request PO");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        disabled={loading}
        className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 hover:text-amber-800 border border-amber-200 hover:border-amber-300 px-2 py-1 rounded transition-colors disabled:opacity-40"
        data-testid={`request-po-btn-${subJobId}`}
      >
        <Envelope size={10} /> {loading ? "Sending…" : "Request PO"}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded shadow-lg py-1 min-w-[180px]">
          {options.map((opt) => (
            <button
              key={opt.mode}
              onClick={(e) => { e.stopPropagation(); handleSelect(opt.mode); }}
              className="w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 text-slate-700"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SendInvoiceButton({ projectId, subJobId, invoiceStatus, onDone }) {
  const [loading, setLoading] = useState(false);

  const isTerminal = invoiceStatus === "AUTHORISED" || invoiceStatus === "PAID";

  const handleClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await sendInvoice(projectId, subJobId);
      toast.success("Invoice sent via Xero");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading || isTerminal}
      className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-300 px-2 py-1 rounded transition-colors disabled:opacity-40"
      data-testid={`send-invoice-btn-${subJobId}`}
    >
      <CurrencyDollar size={10} /> {loading ? "Sending…" : "Send Invoice"}
    </button>
  );
}

function SubJobRow({ job, projectId, onUpdate, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (field, currentValue) => {
    setEditing(field);
    setEditVal(currentValue || "");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditVal("");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await updateSubJob(projectId, job.id, { [editing]: editVal });
      onUpdate(updated);
      toast.success(`Updated ${editing.replace("_", " ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
      setEditing(null);
      setEditVal("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveEdit();
    if (e.key === "Escape") cancelEdit();
  };

  const renderField = (label, field, value) => (
    <div className="flex items-start gap-2 text-xs">
      <span className="w-28 flex-shrink-0 font-medium text-slate-500">{label}</span>
      {editing === field ? (
        <div className="flex items-center gap-1 flex-1">
          <input
            autoFocus
            className="flex-1 text-xs border border-slate-200 rounded px-2 py-0.5 outline-none focus:border-primary bg-white"
            value={editVal}
            onChange={(e) => setEditVal(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button onClick={saveEdit} disabled={saving} className="text-primary hover:text-primary/80">
            <Check size={12} weight="bold" />
          </button>
          <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-700">
            <X size={12} />
          </button>
        </div>
      ) : (
        <span
          className="text-slate-700 cursor-pointer hover:text-primary flex-1"
          onClick={() => startEdit(field, value)}
        >
          {value || <span className="text-slate-300 italic">—</span>}
        </span>
      )}
    </div>
  );

  return (
    <div className="border border-slate-200 rounded" data-testid={`sub-job-row-${job.id}`}>
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <CaretDown size={12} className="text-slate-400" />
        ) : (
          <CaretRight size={12} className="text-slate-400" />
        )}
        <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{job.site_name}</span>
        {job.client_name && <span className="text-xs text-slate-500 hidden sm:inline">{job.client_name}</span>}
        {job.po_number && <span className="text-[10px] text-slate-400 font-mono hidden md:inline">PO: {job.po_number}</span>}
        {job.quoted_price != null && (
          <span className="text-xs font-medium text-slate-600">${Number(job.quoted_price).toLocaleString()}</span>
        )}
        {job.xero_quote_status && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${XERO_QUOTE_BADGES[job.xero_quote_status] || ""}`}>
            {job.xero_quote_status}
          </span>
        )}
        <StatusBadge status={job.status} />
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2 bg-slate-50/50">
          {renderField("Site Name", "site_name", job.site_name)}
          {renderField("Client", "client_name", job.client_name)}
          {renderField("Client Email", "client_email", job.client_email)}
          {renderField("PO Number", "po_number", job.po_number)}
          {renderField("Invoice #", "invoice_number", job.invoice_number)}
          {renderField("Notes", "notes", job.notes)}
          <div className="flex items-start gap-2 text-xs">
            <span className="w-28 flex-shrink-0 font-medium text-slate-500">Quoted Price</span>
            <span className="text-slate-700">
              {job.quoted_price != null ? `$${Number(job.quoted_price).toLocaleString()}` : "—"}
            </span>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <span className="w-28 flex-shrink-0 font-medium text-slate-500">Invoice Amt</span>
            <span className="text-slate-700">
              {job.final_invoice_amount != null ? `$${Number(job.final_invoice_amount).toLocaleString()}` : "—"}
            </span>
          </div>
          {job.xero_invoice_status && (
            <div className="flex items-start gap-2 text-xs">
              <span className="w-28 flex-shrink-0 font-medium text-slate-500">Xero Invoice</span>
              <span className="text-slate-700">{job.xero_invoice_status}</span>
            </div>
          )}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
            {/* PO & Invoice actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <PoRequestDropdown projectId={projectId} subJobId={job.id} poStatus={job.po_status} onDone={() => onUpdate(job)} />
              {job.final_invoice_amount > 0 && (
                <SendInvoiceButton projectId={projectId} subJobId={job.id} invoiceStatus={job.xero_invoice_status} onDone={() => onUpdate(job)} />
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive(job);
              }}
              className="flex items-center gap-1 text-[10px] font-semibold text-red-500 hover:text-red-700 border border-red-200 hover:border-red-300 px-2 py-1 rounded transition-colors"
              data-testid={`archive-sub-job-${job.id}`}
            >
              <Trash size={10} /> Archive
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddSubJobForm({ projectId, onCreated, onCancel }) {
  const [siteName, setSiteName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!siteName.trim()) return;
    setSaving(true);
    try {
      const created = await createSubJob(projectId, {
        site_name: siteName.trim(),
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        po_number: poNumber.trim(),
        notes: notes.trim(),
      });
      toast.success(`Sub-job "${siteName.trim()}" created`);
      onCreated(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create sub-job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-slate-200 rounded p-3 space-y-2 bg-slate-50" data-testid="add-sub-job-form">
      <input
        autoFocus
        required
        className="w-full text-sm border border-slate-200 rounded px-2.5 py-1.5 outline-none focus:border-primary bg-white"
        placeholder="Site name (required)"
        value={siteName}
        onChange={(e) => setSiteName(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
          placeholder="Client name"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />
        <input
          type="email"
          className="text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
          placeholder="Client email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
        />
      </div>
      <input
        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white"
        placeholder="PO number"
        value={poNumber}
        onChange={(e) => setPoNumber(e.target.value)}
      />
      <textarea
        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-primary bg-white resize-none"
        placeholder="Notes (optional)"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !siteName.trim()}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
          data-testid="add-sub-job-submit"
        >
          <Check size={12} weight="bold" /> {saving ? "Creating…" : "Create Sub-job"}
        </button>
      </div>
    </form>
  );
}

export function SubJobsSection({ project }) {
  const [subJobs, setSubJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const projectId = project?.id;

  const loadSubJobs = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getSubJobs(projectId);
      setSubJobs(data);
    } catch {
      // Silent fail — component just shows empty
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadSubJobs();
  }, [loadSubJobs]);

  const handleCreated = (newJob) => {
    setSubJobs((prev) => [newJob, ...prev]);
    setShowAddForm(false);
  };

  const handleUpdate = (updated) => {
    setSubJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;
    try {
      await archiveSubJob(projectId, archiveTarget.id);
      setSubJobs((prev) => prev.filter((j) => j.id !== archiveTarget.id));
      toast.success(`"${archiveTarget.site_name}" archived`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    } finally {
      setArchiveTarget(null);
    }
  };

  if (loading) {
    return null; // Don't flash empty state while loading
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="sub-jobs-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
          <Buildings size={12} className="text-slate-400" />
          Sub-jobs / Sites
        </h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-2 py-1 rounded transition-colors"
          data-testid="add-sub-job-btn"
        >
          <Plus size={10} weight="bold" />
          Add Sub-job
        </button>
      </div>

      {showAddForm && (
        <div className="mb-3">
          <AddSubJobForm
            projectId={projectId}
            onCreated={handleCreated}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {subJobs.length === 0 && !showAddForm ? (
        <p className="text-xs text-slate-400 italic">No sub-jobs yet. Add one to track separate sites or contracts.</p>
      ) : (
        <div className="space-y-2">
          {subJobs.map((job) => (
            <SubJobRow
              key={job.id}
              job={job}
              projectId={projectId}
              onUpdate={handleUpdate}
              onArchive={setArchiveTarget}
            />
          ))}
        </div>
      )}

      {/* Archive confirmation dialog */}
      {archiveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="archive-confirm-dialog">
          <div className="bg-white rounded-lg shadow-xl p-5 max-w-sm w-full mx-4">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Archive sub-job?</h4>
            <p className="text-xs text-slate-600 mb-4">
              This will archive <strong>"{archiveTarget.site_name}"</strong>. It won't appear in the active list but data is preserved.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setArchiveTarget(null)}
                className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                className="text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded transition-colors"
                data-testid="archive-confirm-btn"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
